import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["SERVICE", "UTILITY", "MARKETING", "AUTHENTICATION", "AUTHENTICATION_INTERNATIONAL", "OTHER"];
const EMPTY_META = { category: "UTILITY", meta_rate: "", effective_from: "", country: "BR", currency: "BRL", status: "ativo", observacao: "" };
const EMPTY_CUST = { plan_id: "", category: "UTILITY", customer_rate: "", effective_from: "", status: "ativo", observacao: "" };

export default function AdminWhatsAppPricing() {
  const [metaRates, setMetaRates] = useState([]);
  const [customer, setCustomer] = useState({ rates: [], plans: [] });
  const [alertPcts, setAlertPcts] = useState("70, 80, 90");
  const [open, setOpen] = useState(null); // 'meta' | 'customer'
  const [editing, setEditing] = useState(null);
  const [metaForm, setMetaForm] = useState(EMPTY_META);
  const [custForm, setCustForm] = useState(EMPTY_CUST);

  const load = () => {
    api.get("/admin/pricing/meta").then((r) => setMetaRates(r.data)).catch(() => {});
    api.get("/admin/pricing/customer").then((r) => setCustomer(r.data)).catch(() => {});
    api.get("/admin/settings/consumption").then((r) => setAlertPcts(r.data.alert_pcts.join(", "))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNewMeta = () => { setEditing(null); setMetaForm(EMPTY_META); setOpen("meta"); };
  const openEditMeta = (r) => {
    setEditing(r);
    setMetaForm({ category: r.category, meta_rate: r.meta_rate, effective_from: r.effective_from,
                  country: r.country || "BR", currency: r.currency || "BRL",
                  status: r.status || "ativo", observacao: r.observacao || "" });
    setOpen("meta");
  };
  const openNewCust = () => { setEditing(null); setCustForm(EMPTY_CUST); setOpen("customer"); };
  const openEditCust = (r) => {
    setEditing(r);
    setCustForm({ plan_id: r.plan_id, category: r.category, customer_rate: r.customer_rate,
                  effective_from: r.effective_from, status: r.status || "ativo", observacao: r.observacao || "" });
    setOpen("customer");
  };

  const saveMeta = async (e) => {
    e.preventDefault();
    const payload = { ...metaForm, meta_rate: Number(metaForm.meta_rate) };
    try {
      if (editing) await api.patch(`/admin/pricing/meta/${editing.id}`, payload);
      else await api.post("/admin/pricing/meta", payload);
      toast.success(editing ? "Tarifa Meta atualizada" : "Tarifa Meta registrada (versionada)");
      setOpen(null); setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveCustomer = async (e) => {
    e.preventDefault();
    const payload = { ...custForm, customer_rate: Number(custForm.customer_rate) };
    try {
      if (editing) await api.patch(`/admin/pricing/customer/${editing.id}`, payload);
      else await api.post("/admin/pricing/customer", payload);
      toast.success(editing ? "Preço RAVI atualizado" : "Preço RAVI registrado para o plano");
      setOpen(null); setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const deactivate = async (kind, r) => {
    try {
      if (kind === "meta") {
        await api.patch(`/admin/pricing/meta/${r.id}`, {
          category: r.category, meta_rate: r.meta_rate, effective_from: r.effective_from,
          country: r.country || "BR", currency: r.currency || "BRL", status: "inativo", observacao: r.observacao || "" });
      } else {
        await api.patch(`/admin/pricing/customer/${r.id}`, {
          plan_id: r.plan_id, category: r.category, customer_rate: r.customer_rate,
          effective_from: r.effective_from, status: "inativo", observacao: r.observacao || "" });
      }
      toast.success("Tarifa desativada — o histórico de períodos anteriores foi preservado");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveAlerts = async () => {
    try {
      const pcts = alertPcts.split(",").map((s) => Number(s.trim())).filter((n) => n >= 1 && n <= 100);
      await api.put("/admin/settings/consumption", { alert_pcts: pcts });
      toast.success("Alertas de consumo configurados");
    } catch (err) { toast.error(apiError(err)); }
  };

  const statusBadge = (s) => s === "inativo"
    ? <span className="text-[10px] text-zinc-500 border border-zinc-700 rounded px-1.5 py-0.5">inativa</span>
    : <span className="text-[10px] text-emerald-400 border border-emerald-800/50 rounded px-1.5 py-0.5">ativa</span>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-pricing-page">
      <div className="fade-up">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-100">Tarifas WhatsApp</h1>
        <p className="text-sm text-zinc-500 mt-1">Custo Meta de referência e preço RAVI por plano — tudo versionado, nada fixo em código. Tarifas históricas nunca são apagadas.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 fade-up">
        <div className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5" data-testid="meta-pricing-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-zinc-200 text-sm">Custo Meta (referência)</h3>
            <Button size="sm" variant="outline" onClick={openNewMeta} data-testid="add-meta-rate-btn" className="border-[#3F476C] text-zinc-300">
              <Plus size={13} className="mr-1" /> Nova tarifa
            </Button>
          </div>
          {metaRates.length === 0 && <p className="text-xs text-zinc-600">Nenhuma tarifa configurada.</p>}
          <div className="space-y-2">
            {metaRates.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-[#23283E] bg-[#090A0F] px-3.5 py-2.5" data-testid={`meta-rate-${r.id}`}>
                <div>
                  <p className="text-xs text-zinc-200 flex items-center gap-2">{r.category} <span className="text-zinc-600">({r.country} • {r.currency})</span> {statusBadge(r.status)}</p>
                  <p className="text-[10px] text-zinc-600">vigente desde {r.effective_from} • {r.pricing_unit}{r.observacao ? ` • ${r.observacao}` : ""}</p>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-sm font-mono-code text-amber-300">$ {r.meta_rate}</span>
                  <button onClick={() => openEditMeta(r)} data-testid={`edit-meta-rate-${r.id}`} title="Editar"
                    className="text-zinc-600 hover:text-zinc-200 transition-colors"><Pencil size={13} /></button>
                  {r.status !== "inativo" && (
                    <button onClick={() => deactivate("meta", r)} data-testid={`deactivate-meta-rate-${r.id}`} title="Desativar (preserva histórico)"
                      className="text-zinc-600 hover:text-rose-400 transition-colors"><Ban size={13} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5" data-testid="customer-pricing-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-zinc-200 text-sm">Preço RAVI por plano</h3>
            <Button size="sm" variant="outline" onClick={openNewCust} data-testid="add-customer-rate-btn" className="border-[#3F476C] text-zinc-300">
              <Plus size={13} className="mr-1" /> Novo preço
            </Button>
          </div>
          {customer.rates.length === 0 && <p className="text-xs text-zinc-600">Nenhum preço configurado.</p>}
          <div className="space-y-2">
            {customer.rates.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-[#23283E] bg-[#090A0F] px-3.5 py-2.5" data-testid={`customer-rate-${r.id}`}>
                <div>
                  <p className="text-xs text-zinc-200 flex items-center gap-2">{r.plan_name} <span className="text-zinc-600">• {r.category}</span> {statusBadge(r.status)}</p>
                  <p className="text-[10px] text-zinc-600">vigente desde {r.effective_from}{r.observacao ? ` • ${r.observacao}` : ""}</p>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-sm font-mono-code text-emerald-300">R$ {r.customer_rate}</span>
                  <button onClick={() => openEditCust(r)} data-testid={`edit-customer-rate-${r.id}`} title="Editar"
                    className="text-zinc-600 hover:text-zinc-200 transition-colors"><Pencil size={13} /></button>
                  {r.status !== "inativo" && (
                    <button onClick={() => deactivate("customer", r)} data-testid={`deactivate-customer-rate-${r.id}`} title="Desativar (preserva histórico)"
                      className="text-zinc-600 hover:text-rose-400 transition-colors"><Ban size={13} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5 fade-up max-w-md" data-testid="consumption-alerts-card">
        <h3 className="font-display font-semibold text-zinc-200 text-sm mb-1.5">Alertas de consumo</h3>
        <p className="text-[11px] text-zinc-600 mb-3">Percentuais do limite de mensagens do plano que geram alerta ao escritório (nunca bloqueia).</p>
        <div className="flex gap-2">
          <Input value={alertPcts} onChange={(e) => setAlertPcts(e.target.value)} data-testid="alert-pcts-input"
            placeholder="70, 80, 90" className="bg-[#090A0F] border-[#23283E]" />
          <Button onClick={saveAlerts} data-testid="alert-pcts-save" className="brand-gradient brand-gradient-hover text-white border-0 shrink-0">Salvar</Button>
        </div>
      </div>

      <Dialog open={open === "meta"} onOpenChange={() => { setOpen(null); setEditing(null); }}>
        <DialogContent className="bg-[#0F111A] border-[#23283E]" data-testid="meta-rate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-zinc-100">{editing ? "Editar tarifa Meta" : "Nova tarifa Meta"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Referência versionada — tarifas antigas nunca são sobrescritas nem apagadas.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveMeta} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Categoria</Label>
                <Select value={metaForm.category} onValueChange={(v) => setMetaForm({ ...metaForm, category: v })}>
                  <SelectTrigger data-testid="meta-rate-category" className="bg-[#090A0F] border-[#23283E]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F111A] border-[#23283E]">
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Custo Meta (por mensagem)</Label>
                <Input type="number" step="0.0001" required value={metaForm.meta_rate}
                  onChange={(e) => setMetaForm({ ...metaForm, meta_rate: e.target.value })}
                  data-testid="meta-rate-value" placeholder="0.0068" className="bg-[#090A0F] border-[#23283E]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Vigente a partir de</Label>
                <Input type="date" required value={metaForm.effective_from}
                  onChange={(e) => setMetaForm({ ...metaForm, effective_from: e.target.value })}
                  data-testid="meta-rate-from" className="bg-[#090A0F] border-[#23283E]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Status</Label>
                <Select value={metaForm.status} onValueChange={(v) => setMetaForm({ ...metaForm, status: v })}>
                  <SelectTrigger data-testid="meta-rate-status" className="bg-[#090A0F] border-[#23283E]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F111A] border-[#23283E]">
                    <SelectItem value="ativo">Ativa</SelectItem>
                    <SelectItem value="inativo">Inativa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Observação (opcional)</Label>
              <Input value={metaForm.observacao} onChange={(e) => setMetaForm({ ...metaForm, observacao: e.target.value })}
                data-testid="meta-rate-obs" placeholder="Ex: reajuste Meta out/2026" className="bg-[#090A0F] border-[#23283E]" />
            </div>
            <Button type="submit" data-testid="meta-rate-save" className="w-full brand-gradient brand-gradient-hover text-white border-0">
              {editing ? "Salvar alterações" : "Registrar tarifa"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={open === "customer"} onOpenChange={() => { setOpen(null); setEditing(null); }}>
        <DialogContent className="bg-[#0F111A] border-[#23283E]" data-testid="customer-rate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-zinc-100">{editing ? "Editar preço RAVI" : "Preço RAVI por plano"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Valor comercial definido pelo RAVI — independente do custo Meta.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveCustomer} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Plano</Label>
                <Select value={custForm.plan_id} onValueChange={(v) => setCustForm({ ...custForm, plan_id: v })}>
                  <SelectTrigger data-testid="customer-rate-plan" className="bg-[#090A0F] border-[#23283E]"><SelectValue placeholder="Selecionar…" /></SelectTrigger>
                  <SelectContent className="bg-[#0F111A] border-[#23283E]">
                    {customer.plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Categoria</Label>
                <Select value={custForm.category} onValueChange={(v) => setCustForm({ ...custForm, category: v })}>
                  <SelectTrigger data-testid="customer-rate-category" className="bg-[#090A0F] border-[#23283E]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F111A] border-[#23283E]">
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Preço RAVI (R$/mensagem)</Label>
                <Input type="number" step="0.0001" required value={custForm.customer_rate}
                  onChange={(e) => setCustForm({ ...custForm, customer_rate: e.target.value })}
                  data-testid="customer-rate-value" placeholder="0.05" className="bg-[#090A0F] border-[#23283E]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Vigente a partir de</Label>
                <Input type="date" required value={custForm.effective_from}
                  onChange={(e) => setCustForm({ ...custForm, effective_from: e.target.value })}
                  data-testid="customer-rate-from" className="bg-[#090A0F] border-[#23283E]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Status</Label>
                <Select value={custForm.status} onValueChange={(v) => setCustForm({ ...custForm, status: v })}>
                  <SelectTrigger data-testid="customer-rate-status" className="bg-[#090A0F] border-[#23283E]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F111A] border-[#23283E]">
                    <SelectItem value="ativo">Ativa</SelectItem>
                    <SelectItem value="inativo">Inativa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Observação (opcional)</Label>
                <Input value={custForm.observacao} onChange={(e) => setCustForm({ ...custForm, observacao: e.target.value })}
                  data-testid="customer-rate-obs" placeholder="Ex: margem promocional" className="bg-[#090A0F] border-[#23283E]" />
              </div>
            </div>
            <Button type="submit" data-testid="customer-rate-save" className="w-full brand-gradient brand-gradient-hover text-white border-0">
              {editing ? "Salvar alterações" : "Registrar preço"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
